import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Row from "@amzn/meridian/esm/components/row";
import Column from "@amzn/meridian/esm/components/column";
import HelpTabInClient from "./helpTab/HelpTabInClient";
import SettingsTabInClient from "./settingsTab/SettingsTabInClient";
import SummariesTabInClient from "./summaryTab/SummariesTabInClient";
import SidebarInClient from "./common/SidebarInClient";
import { CLIENT_TAB } from "./inMainClient.constants";
import { fetchPaginatedSummaries } from "../../../utils/summaryUtils";
import LogManager from "../../../utils/logManager";
import { SESSION_STORAGE_KEYS } from "../../../constants";

/**
 * MainClientHome Component
 *
 * This component serves as the main entry UI for users in the "MainClient" mode.
 * It features a side menu with two selectable tabs: "Summaries" and "Help".
 *
 * Functionality:
 * - When "Summaries" is selected, it displays the SummariesTabInClient component,
 *   showing the list of meeting summaries or related content.
 * - When "Help" is selected, it renders the HelpTabInClient component,
 *   which includes InfoCards for user feedback, documentation (wiki) and Amazon policy on using AI in meetings.
 *
 * The component uses state to manage the selected tab and dynamically renders
 * the appropriate content based on the user's selection.
 */
const MainClientHome = ({ featureFlags }) => {
  const location = useLocation();
  const initialTab = location.state?.selectedTab || CLIENT_TAB.SUMMARIES;
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const returnPage = location.state?.returnPage ?? 1;
  const [isPrefetchComplete, setIsPrefetchComplete] = useState(false);
  const isVerboseSummariesEnabled = featureFlags?.isVerboseSummariesEnabled;

  // Prefetch summaries data on component mount (like Home.jsx pattern)
  useEffect(() => {
    // Clear all pagination state to force fresh fetch
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA);

    // Always fetch fresh data from backend on mount/refresh
    fetchPaginatedSummaries(true, null, 10)
    .then(result => {
      setIsPrefetchComplete(true);
    })
    .catch(error => {
      LogManager.error("Failed to prefetch summaries in MainClientHome:", error);
      setIsPrefetchComplete(true);
    });
  }, []);

  return (
    <Row spacing="none" spacingInset="none" alignmentVertical="top" widths={["fit", "fill"]}>
      <SidebarInClient selectedTab={selectedTab} setSelectedTab={setSelectedTab} isVerboseSummariesEnabled={isVerboseSummariesEnabled} />

      <Column width="100%" alignmentHorizontal="center">
        {selectedTab === CLIENT_TAB.SETTINGS && <SettingsTabInClient />}
        {selectedTab === CLIENT_TAB.HELP && <HelpTabInClient />}
        {selectedTab === CLIENT_TAB.SUMMARIES && (
          <SummariesTabInClient returnPage={returnPage} isPrefetchComplete={isPrefetchComplete} isAttendanceReportEnabled={featureFlags?.isAttendanceReportEnabled} isVerboseSummariesEnabled={isVerboseSummariesEnabled} />
        )}
      </Column>
    </Row>
  );
};

export default MainClientHome;
